import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ModifyTripModal = ({ isOpen, onClose, onConfirm, tripData }) => {
  const [modificationRequest, setModificationRequest] = useState('');
  const [modificationCategory, setModificationCategory] = useState('');

  const categories = [
    { value: 'dates', label: 'Change Dates', icon: 'Calendar' },
    { value: 'activities', label: 'Modify Activities', icon: 'Activity' },
    { value: 'accommodation', label: 'Update Accommodation', icon: 'Home' },
    { value: 'duration', label: 'Adjust Duration', icon: 'Clock' },
    { value: 'budget', label: 'Change Budget', icon: 'DollarSign' },
    { value: 'other', label: 'Other Changes', icon: 'Edit' }
  ];

  const handleConfirm = () => {
    if (!modificationRequest?.trim()) {
      return;
    }

    const modificationData = {
      category: modificationCategory,
      request: modificationRequest,
      originalTripData: tripData
    };

    onConfirm(modificationData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-organic max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-organic-lg">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary/10 rounded-organic flex items-center justify-center">
                <Icon name="Edit" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="font-heading font-heading-bold text-xl text-card-foreground">
                  Modify Your Trip
                </h3>
                <p className="text-sm text-muted-foreground">
                  Tell us what you would like to change about your trip
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-organic transition-colors"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Modification Category Selection */}
          <div>
            <label className="block text-sm font-body font-body-medium text-card-foreground mb-3">
              What would you like to modify?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories?.map((category) => (
                <button
                  key={category?.value}
                  onClick={() => setModificationCategory(category?.value)}
                  className={`p-4 rounded-organic border-2 transition-all ${
                    modificationCategory === category?.value
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Icon 
                      name={category?.icon} 
                      size={24} 
                      color={modificationCategory === category?.value ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} 
                    />
                    <span className={`text-xs font-body ${
                      modificationCategory === category?.value
                        ? 'text-primary font-body-medium' :'text-muted-foreground'
                    }`}>
                      {category?.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Modification Details */}
          <div>
            <label className="block text-sm font-body font-body-medium text-card-foreground mb-2">
              Describe your changes
            </label>
            <textarea
              value={modificationRequest}
              onChange={(e) => setModificationRequest(e?.target?.value)}
              placeholder="Example: I want to extend the trip by 2 days and add more hiking trails..."
              className="w-full h-32 px-4 py-3 border border-border rounded-organic focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Be as specific as possible so our AI can generate the best modifications for you
            </p>
          </div>

          {/* Current Trip Summary */}
          <div className="p-4 bg-muted/30 rounded-organic">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Info" size={16} color="var(--color-primary)" />
              <span className="text-sm font-body font-body-medium text-card-foreground">
                Current Trip Details
              </span>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>📍 Destination: {tripData?.featuredParks?.[0] || 'N/A'}</p>
              <p>📅 Duration: {tripData?.duration || 'N/A'}</p>
              <p>👥 Travelers: {tripData?.travelers || 2} people</p>
              <p>💰 Budget: {tripData?.estimatedCost || 'N/A'}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={!modificationRequest?.trim() || !modificationCategory}
              iconName="Check"
              iconPosition="left"
              className="flex-1"
            >
              Confirm Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifyTripModal;